import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect } from 'react';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { U2000TableHandlers } from '../../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3303Context } from '../../U3303Context';
import { doDeleteHolidayYear, doSearchHolidayYears, luHandleAppiError, type U3303HolidayYearSearchRow } from '../../U3303Service';
import { NS } from '../../U3303';
import U3303HolidayYearDeleteDlg from '../common/U3303HolidayYearDeleteDlg';
import { U3303HolidayYearListToolBar } from './U3303HolidayYearListToolBar';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'year',
      label: t('lblYear', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'number',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: 'DESC',
    },
    {
      name: 'dayCount',
      label: t('lblDayCount', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'number',
      align: 'right',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
  ];
}

const U3303HolidayYearList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3303Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  useEffect(() => {
    const cols = getColumnsDef(t);
    searchStateHandlers.setColumns(cols);
    gridStateHandlers.setColumns(cols);
    setLogicalUnitBreadcrumb([{ label: t('title', { ns: NS }) }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3303HolidayYearSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearchHolidayYears(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3303HolidayYearSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (_id, row) => {
      return await doDeleteHolidayYear(u2000AxiosAuthenticated, { id: row.id, version: row.version });
    },
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: String(row.year), ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const listHandlerActions: U2000TableHandlers<U3303HolidayYearSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3303HolidayYearSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`/U3303/${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3303');
      navigate('/U3303/new');
    },
    onModify: () => {
      const row = gridStateHandlers.getSelectedRows()[0];
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3303');
      navigate(`/U3303/${row?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <U3303HolidayYearDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3303HolidayYearListToolBar gridStateHandlers={gridStateHandlers} handlerActions={listHandlerActions} />}
        handlerActions={listHandlerActions}
        label={t('lblHolidayYearList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </div>
  );
};

export default U3303HolidayYearList;
